import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LocationOnIcon from "@mui/icons-material/LocationOn";

// Components & API
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import BackArrowIcon from "../../../../components/icons/BackArrowIcon";
import SearchIcon from "../../../../components/icons/SearchIcon";
import LoadingIcon from "../../../../components/icons/LoadingIcon";
import { PropertyApi } from "../../../../utils/api/Property";
import { getAgencyId, getAgentId } from "../../../../utils/helpers/common";
import { messages } from "../../../../config/messages";
import "./CreatePropertyBank.scss";
import ErrorSummary from "../error/ErrorSummary";

declare global {
  interface Window {
    google: any;
  }
}

interface PropertyType {
  _id: string;
  name: string;
  slug?: string;
}

interface SizeUnit {
  _id: string;
  name: string;
  symbol?: string;
}

const CreatePropertyBank = () => {
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [searchOptions, setSearchOptions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const debounceTimeout = useRef<any>(null);
  const autocompleteService = useRef<any>(null);
  const placesService = useRef<any>(null);

  const [state, setState] = useState<{
    isLoading: boolean;
    isLoadingSelect: boolean;
    propertyType: {
      commercial: PropertyType[];
      residential: PropertyType[];
    };
    landSizeUnit: SizeUnit[];
    floorAreaUnit: SizeUnit[];
  }>({
    isLoading: false,
    isLoadingSelect: false,
    propertyType: { commercial: [], residential: [] },
    landSizeUnit: [],
    floorAreaUnit: [],
  });

  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement("div"),
      );
    }
  }, []);

  const initialValues = {
    searchAddress: "",
    unit: "",
    no: "",
    streetName: "",
    city: "",
    state: "",
    country: "",
    postCode: "",

    propertyType: "",
    landSize: "",
    landSizeUnit: "",
    floorArea: "",
    floorAreaUnit: "",
    bedRooms: "",
    bathRooms: "",
    living: "",
    garage: "",
  };

  const validationSchema = useMemo(() => {
    return Yup.object({
      city: Yup.string().required(messages?.errorMessage),
      state: Yup.string().required(messages?.errorMessage),
      country: Yup.string().required(messages?.errorMessage),
      postCode: Yup.string().required(messages?.errorMessage),
      propertyType: Yup.string().required(messages?.homeType),

      bedRooms: Yup.string().required("Bedrooms are required"),
      bathRooms: Yup.string().required("Bathrooms are required"),

      landSize: Yup.string().test(
        "land-required",
        "Land size is required",
        function (val) {
          const { propertyType } = this.parent;
          if (!propertyType) return true;

          const allTypes = [
            ...state.propertyType.residential,
            ...state.propertyType.commercial,
          ];
          const selectedType = allTypes.find((t) => t._id === propertyType);

          if (!selectedType) return true;

          const { showLand } = getFieldVisibility(selectedType.name);

          if (showLand && (!val || val.trim() === "")) return false;
          return true;
        },
      ),

      landSizeUnit: Yup.string().test(
        "land-unit-required",
        "Land size unit is required",
        function (val) {
          const { propertyType } = this.parent;
          if (!propertyType) return true;

          const allTypes = [
            ...state.propertyType.residential,
            ...state.propertyType.commercial,
          ];
          const selectedType = allTypes.find((t) => t._id === propertyType);
          if (!selectedType) return true;

          const { showLand } = getFieldVisibility(selectedType.name);

          if (showLand && (!val || val.trim() === "")) return false;
          return true;
        },
      ),

      floorArea: Yup.string().test(
        "floor-required",
        "Floor area is required",
        function (val) {
          const { propertyType } = this.parent;
          if (!propertyType) return true;

          const allTypes = [
            ...state.propertyType.residential,
            ...state.propertyType.commercial,
          ];
          const selectedType = allTypes.find((t) => t._id === propertyType);
          if (!selectedType) return true;

          const { showFloor } = getFieldVisibility(selectedType.name);

          if (showFloor && (!val || val.trim() === "")) return false;
          return true;
        },
      ),

      floorAreaUnit: Yup.string().test(
        "floor-unit-required",
        "Floor area unit is required",
        function (val) {
          const { propertyType } = this.parent;
          if (!propertyType) return true;

          const allTypes = [
            ...state.propertyType.residential,
            ...state.propertyType.commercial,
          ];
          const selectedType = allTypes.find((t) => t._id === propertyType);
          if (!selectedType) return true;

          const { showFloor } = getFieldVisibility(selectedType.name);

          if (showFloor && (!val || val.trim() === "")) return false;
          return true;
        },
      ),
    });
  }, [state.propertyType]);

  const handleAddressSearch = (value: string) => {
    if (value.length <= 0) {
      setSearchOptions([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    setShowDropdown(true);

    if (!autocompleteService.current) {
      setIsSearching(false);
      return;
    }

    autocompleteService.current.getPlacePredictions(
      {
        input: value,
        componentRestrictions: { country: ["au", "nz", "gb"] },
        types: ["address"],
      },
      (predictions: any[], status: any) => {
        setIsSearching(false);
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          const formattedGoogle = predictions.map((item: any) => ({
            id: item.place_id,
            displayLabel: item.description,
            originalData: item,
          }));
          setSearchOptions(formattedGoogle);
        } else {
          setSearchOptions([]);
        }
      },
    );
  };

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    formik.setFieldValue("searchAddress", val);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => handleAddressSearch(val), 400);
  };

  const handleSelectOption = (option: any) => {
    setShowDropdown(false);
    formik.setFieldValue("searchAddress", option.displayLabel);

    if (placesService.current) {
      placesService.current.getDetails(
        {
          placeId: option.id,
          fields: ["address_components", "formatted_address"],
        },
        (place: any, status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            fillGoogleData(place);
          }
        },
      );
    }
  };

  const fillGoogleData = (place: any) => {
    if (!place.address_components) return;

    const get = (type: string) =>
      place.address_components?.find((c: any) => c.types.includes(type))
        ?.long_name || "";

    formik.setFieldValue("unit", get("subpremise"));
    formik.setFieldValue("no", get("street_number"));
    formik.setFieldValue("streetName", get("route"));

    const city =
      get("locality") ||
      get("postal_town") ||
      get("sublocality_level_1") ||
      get("administrative_area_level_2");

    formik.setFieldValue("city", city);

    const stateVal =
      get("administrative_area_level_1") || get("administrative_area_level_2");
    formik.setFieldValue("state", stateVal);

    formik.setFieldValue("country", get("country") || "Australia");
    formik.setFieldValue("postCode", get("postal_code"));
  };

  useEffect(() => {
    const fetchSelectors = async () => {
      setState((prev) => ({ ...prev, isLoadingSelect: true }));
      try {
        const res = await PropertyApi.getSelectors();
        if (res?.data?.success) {
          const data = res.data.data;
          setState((prev) => ({
            ...prev,
            isLoadingSelect: false,
            propertyType: data.property_types || {
              commercial: [],
              residential: [],
            },
            landSizeUnit: data.land_size_units || [],
            floorAreaUnit: data.land_size_units || [],
          }));
        }
      } catch (err) {
        setState((prev) => ({ ...prev, isLoadingSelect: false }));
      }
    };
    fetchSelectors();
  }, []);

  const onSubmit = async (values: any) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    const payload = {
      unit: values.unit || "",
      property_no: values.no || "",
      property_no_and_street_name: values.streetName || "",

      city: values.city || "",
      state: values.state || "",
      country: values.country || "",
      post_code: values.postCode || "",
      property_address: values.searchAddress || "",

      property_type_id: values.propertyType || null,

      land_area: values.landSize ? Number(values.landSize) : 0,
      land_size_unit_id: values.landSizeUnit || null,

      floor_area: values.floorArea ? Number(values.floorArea) : 0,
      floor_area_unit_id: values.floorAreaUnit || null,

      bedrooms: values.bedRooms ? Number(values.bedRooms) : 0,
      bathrooms: values.bathRooms ? Number(values.bathRooms) : 0,
      garage: values.garage ? Number(values.garage) : 0,
      living: values.living ? Number(values.living) : 0,

      status: "admin_draft",
    };

    try {
      const res = await PropertyApi.createPropertyBank(payload);

      if (res?.data?.success) {
        toast.success("Property Bank Record Created!");
        navigate("/property-bank");
      } else {
        toast.error(res?.data?.message || "Failed to create record.");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const getSelectedTypeName = () => {
    if (!formik.values.propertyType) return "";

    const list = [
      ...(state.propertyType.residential || []),
      ...(state.propertyType.commercial || []),
    ];

    const found = list.find((t) => t._id === formik.values.propertyType);

    return found?.name || "";
  };

  const selectedTypeName = getSelectedTypeName();

  const getFieldVisibility = (typeName: string) => {
    const name = typeName.toLowerCase();

    if (
      name.includes("house") ||
      name.includes("townhouse") ||
      name.includes("villa") ||
      name.includes("land") ||
      name.includes("rural") ||
      name.includes("acreage") ||
      name.includes("block of units")
    ) {
      return { showLand: true, showFloor: false };
    }

    if (
      name.includes("unit") ||
      name.includes("apartment") ||
      name.includes("retirement") ||
      name.includes("studio")
    ) {
      return { showLand: false, showFloor: true };
    }

    return { showLand: true, showFloor: true };
  };

  const { showLand, showFloor } = getFieldVisibility(selectedTypeName);

  useEffect(() => {
    if (!state.landSizeUnit?.length) return;

    const country = (formik.values.country || "").toLowerCase();

    const isNZ = country.includes("new zealand") || country === "nz";

    const isAU = country.includes("australia") || country === "au";

    const targetUnit = state.landSizeUnit.find(
      (u: any) =>
        u.name.toLowerCase().includes("sqm") ||
        u.name.toLowerCase().includes("square meter"),
    );

    if (targetUnit) {
      if (showLand && !formik.values.landSizeUnit) {
        formik.setFieldValue("landSizeUnit", targetUnit._id);
      }

      if (showFloor && !formik.values.floorAreaUnit) {
        formik.setFieldValue("floorAreaUnit", targetUnit._id);
      }
    }
  }, [formik.values.country, state.landSizeUnit, showLand, showFloor]);

  useEffect(() => {
    if (!showLand) {
      formik.setFieldValue("landSize", "");
      formik.setFieldValue("landSizeUnit", "");
    }

    if (!showFloor) {
      formik.setFieldValue("floorArea", "");
      formik.setFieldValue("floorAreaUnit", "");
    }
  }, [showLand, showFloor]);

  const shouldShowErrors =
    formik.submitCount > 0 && Object.keys(formik.errors).length > 0;

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="create-property-bank-container properties-container">
          {/* Header */}
          <div
            className="propertie-view-back-btn"
            onClick={() => navigate("/property-bank")}
          >
            <div className="back-icon">
              <BackArrowIcon />
            </div>
            <p className="bakc-text-contain">Back to property bank list</p>
          </div>
          <p className="main-title">Create Property Bank Record</p>

          <form onSubmit={formik.handleSubmit}>
            <div className="content-title font-inter-500">Property address</div>
            <div className="content-subtitle font-inter-400">
              Enter the address details for new property bank record.
            </div>

            <div className="search-input-container">
              <span className="search-icon-wrapper">
                <SearchIcon />
              </span>
              <input
                ref={searchInputRef}
                type="text"
                className="default-input-contain with-search-icon"
                placeholder="Search Address"
                name="searchAddress"
                value={formik.values.searchAddress}
                onChange={onSearchInputChange}
                autoComplete="off"
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                onFocus={() =>
                  formik.values.searchAddress.length > 2 &&
                  setShowDropdown(true)
                }
              />

              {showDropdown && (searchOptions.length > 0 || isSearching) && (
                <div className="custom-dropdown-list">
                  {isSearching ? (
                    <div className="dropdown-loading">
                      <CircularProgress
                        size={18}
                        style={{ marginRight: 10, color: "#333" }}
                      />{" "}
                      Searching...
                    </div>
                  ) : (
                    <ul>
                      {searchOptions.map((opt, i) => (
                        <li
                          key={i}
                          onClick={() => handleSelectOption(opt)}
                          className="dropdown-item"
                        >
                          <div className="icon-box">
                            <LocationOnIcon
                              sx={{ fontSize: 20, color: "#767279" }}
                            />
                          </div>
                          <div className="text-box">
                            <span className="main-text">
                              {opt.displayLabel}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="double-item-container">
              <div className="input-and-lable-container first-lable">
                <p className="label font-inter-400">
                  Apartment, Suite, Unit, Or Level
                </p>
                <input
                  type="text"
                  className="default-input-contain"
                  placeholder="e.g. Unit 4, Level 2"
                  {...formik.getFieldProps("unit")}
                />
              </div>
              <div className="input-and-lable-container first-lable">
                <p className="label font-inter-400">House Or Building Number</p>
                <input
                  type="text"
                  className="default-input-contain"
                  placeholder="e.g. 22"
                  {...formik.getFieldProps("no")}
                />
              </div>
            </div>

            <div className="double-item-container">
              <div
                className={`input-and-lable-container ${formik.errors.streetName ? "property-input-error" : ""}`}
              >
                <p className="label font-inter-400">Street Name</p>
                <input
                  type="text"
                  className="default-input-contain"
                  placeholder="e.g. West Street"
                  {...formik.getFieldProps("streetName")}
                />
              </div>
              <div
                className={`input-and-lable-container ${formik.errors.city ? "property-input-error" : ""}`}
              >
                <p className="label font-inter-400">City/Suburb*</p>
                <input
                  type="text"
                  className="default-input-contain"
                  placeholder="Select city"
                  {...formik.getFieldProps("city")}
                />
              </div>
            </div>

            <div className="double-item-container">
              <div
                className={`input-and-lable-container ${formik.errors.state ? "property-input-error" : ""}`}
              >
                <p className="label font-inter-400">State*</p>
                <input
                  type="text"
                  className="default-input-contain"
                  placeholder="Enter state (or e.g. NSW)"
                  {...formik.getFieldProps("state")}
                />
              </div>
              <div
                className={`input-and-lable-container ${formik.errors.country ? "property-input-error" : ""}`}
              >
                <p className="label font-inter-400">Country*</p>
                <input
                  type="text"
                  className="default-input-contain"
                  placeholder="Australia"
                  {...formik.getFieldProps("country")}
                />
              </div>
            </div>

            <div className="double-item-container">
              <div
                className={`input-and-lable-container ${formik.errors.postCode ? "property-input-error" : ""}`}
              >
                <p className="label font-inter-400">Postcode*</p>
                <input
                  type="text"
                  className="default-input-contain"
                  placeholder="e.g. 2831"
                  {...formik.getFieldProps("postCode")}
                />
              </div>
            </div>

            <div className="specification-title font-inter-500">
              Property Specifications
            </div>
            <div className="content-subtitle font-inter-400">
              Select the property type and define key features like bedrooms and
              bathrooms to categorize the property bank record.
            </div>

            <div className="double-item-container">
              <div
                className={`input-and-lable-container specification-first-type ${formik.errors.propertyType ? "property-input-selector-border-error" : "property-input-selector-border"}`}
              >
                <p className="label font-inter-400">Property type*</p>
                <FormControl fullWidth>
                  {!formik.values.propertyType && (
                    <InputLabel>Select property type</InputLabel>
                  )}
                  <Select
                    name="propertyType"
                    value={formik.values.propertyType}
                    onChange={formik.handleChange}
                    IconComponent={
                      state.isLoadingSelect
                        ? LoadingIcon
                        : KeyboardArrowDownIcon
                    }
                    className="create-property-forrm-selector"
                    MenuProps={{
                      disablePortal: true,
                      classes: { paper: "selector-menu-container" },
                    }}
                  >
                    {[
                      ...(state.propertyType.residential || []),
                    ].map((t: any) => (
                      <MenuItem key={t._id} value={t._id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            {showLand && (
              <div className="double-item-container">
                <div className="input-and-lable-container property-info-inputs">
                  <p className="label font-inter-400">Land size unit*</p>
                  <FormControl
                    fullWidth
                    className="property-input-selector-border"
                  >
                    {!formik.values.landSizeUnit && (
                      <InputLabel>Square meter</InputLabel>
                    )}
                    <Select
                      name="landSizeUnit"
                      value={formik.values.landSizeUnit}
                      onChange={formik.handleChange}
                      IconComponent={KeyboardArrowDownIcon}
                      className="create-property-forrm-selector"
                      MenuProps={{
                        disablePortal: true,
                        classes: { paper: "selector-menu-container" },
                      }}
                    >
                      {state.landSizeUnit.map((u: any) => (
                        <MenuItem key={u._id} value={u._id}>
                          {u.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className="input-and-lable-container property-info-inputs">
                  <p className="label font-inter-400">Land size*</p>
                  <input
                    type="text"
                    className="default-input-contain"
                    placeholder="e.g. 550"
                    {...formik.getFieldProps("landSize")}
                  />
                </div>
              </div>
            )}
            {showFloor && (
              <div className="double-item-container">
                <div className="input-and-lable-container property-info-inputs">
                  <p className="label font-inter-400">Floor area unit*</p>
                  <FormControl
                    fullWidth
                    className="property-input-selector-border"
                  >
                    {!formik.values.floorAreaUnit && (
                      <InputLabel>Square meter</InputLabel>
                    )}
                    <Select
                      name="floorAreaUnit"
                      value={formik.values.floorAreaUnit}
                      onChange={formik.handleChange}
                      IconComponent={KeyboardArrowDownIcon}
                      className="create-property-forrm-selector"
                      MenuProps={{
                        disablePortal: true,
                        classes: { paper: "selector-menu-container" },
                      }}
                    >
                      {state.floorAreaUnit.map((u: any) => (
                        <MenuItem key={u._id} value={u._id}>
                          {u.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className="input-and-lable-container property-info-inputs">
                  <p className="label font-inter-400">Floor area*</p>
                  <input
                    type="text"
                    className="default-input-contain"
                    placeholder="e.g. 550"
                    {...formik.getFieldProps("floorArea")}
                  />
                </div>
              </div>
            )}
            <div className="double-item-container">
              <div className="input-and-lable-container property-info-inputs">
                <p className="label font-inter-400">Bedrooms*</p>
                <input
                  type="text"
                  className="default-input-contain"
                  placeholder="0"
                  {...formik.getFieldProps("bedRooms")}
                />
              </div>
              <div className="input-and-lable-container property-info-inputs">
                <p className="label font-inter-400">Bathrooms*</p>
                <input
                  type="text"
                  className="default-input-contain"
                  placeholder="0"
                  {...formik.getFieldProps("bathRooms")}
                />
              </div>
            </div>

            <div className="double-item-container">
              <div className="input-and-lable-container property-info-inputs">
                <p className="label font-inter-400">Living/Study</p>
                <input
                  type="text"
                  className="default-input-contain"
                  placeholder="0"
                  {...formik.getFieldProps("living")}
                />
              </div>
              <div className="input-and-lable-container property-info-inputs">
                <p className="label font-inter-400">Garage/Parking</p>
                <input
                  type="text"
                  className="default-input-contain"
                  placeholder="0"
                  {...formik.getFieldProps("garage")}
                />
              </div>
            </div>

            {shouldShowErrors && (
              <div className="error-message-component">
                <ErrorSummary
                  errors={formik.errors as Record<string, string>}
                />
              </div>
            )}

            <div className="create-bank-button-container">
              <button
                type="button"
                className="create-bank-cancel-property-button font-inter-400"
                onClick={() => navigate("/property-bank")}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="create-bank-property-button font-inter-500"
                disabled={state.isLoading}
              >
                {state.isLoading ? "Creating..." : "Create"}
              </button>
            </div>
          </form>
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default CreatePropertyBank;
